/**
 * Assign subject & faculty to the active semester (admin)
 */

import AllocationMasterFilters from './AllocationMasterFilters';

export default function AssignSubjectFacultyPanel({
  activeCycle,
  assignError,
  assignSubjectFacultyForm,
  setAssignSubjectFacultyForm,
  subjects,
  facultyList,
  divisions,
  submitAssignSubjectFaculty,
  assignSubmitting,
  activeSemesterAssignments,
  browseMode = false,
  departments,
  programs,
  academicYears,
  allocationFilters,
  onAllocationFiltersChange,
  allocationCanShowList,
  allocationListShown,
  allocationListLoading,
  allocationResultCount,
  onShowAllocationList,
  browsedAssignments,
}) {
  const tableRows = browseMode
    ? (allocationListShown ? (browsedAssignments || []) : [])
    : (activeSemesterAssignments || []);
  const showSemesterColumn = browseMode && (allocationFilters?.semester === '' || allocationFilters?.semester == null);
  const semesterDivisions = activeCycle
    ? (divisions || []).filter(
      (d) => d.academic_year === activeCycle.academic_year && d.semester === activeCycle.semester
    )
    : [];
  const semesterSubjects = activeCycle
    ? (subjects || []).filter((s) => Number(s.semester) === Number(activeCycle.semester))
    : [];

  const pageTitle = browseMode
    ? 'Subject & faculty allocation'
    : 'Add subject & faculty to active semester';
  const pageSubtitle = browseMode
    ? 'Filter allocations by department, program, and semester. Manual assignments still use the active feedback cycle.'
    : 'Map subjects to instructors for the currently active feedback cycle.';

  return (
    <div className="assign-panel admin-master-panel">
      <div className="admin-card-body">
        <div className="assign-panel-header">
          <div>
            <h2 className="admin-section-title m-0">{pageTitle}</h2>
            <p className="admin-section-subtitle mb-0 mt-1">
              {pageSubtitle}
            </p>
          </div>
          {activeCycle ? (
            <div className="assign-active-badge">
              <span className="assign-active-badge-label">Active semester</span>
              <span className="assign-active-badge-value">
                {activeCycle.name || `${activeCycle.academic_year} · Sem ${activeCycle.semester}`}
              </span>
            </div>
          ) : (
            <div className="assign-active-badge assign-active-badge--muted">
              <span className="assign-active-badge-label">Active semester</span>
              <span className="assign-active-badge-value">None</span>
            </div>
          )}
        </div>

        {browseMode && allocationFilters && onAllocationFiltersChange && (
          <div className="mt-4">
            <AllocationMasterFilters
              allocationFilters={allocationFilters}
              onFiltersChange={onAllocationFiltersChange}
              departments={departments}
              programs={programs}
              academicYears={academicYears}
              canShowList={allocationCanShowList}
              listShown={allocationListShown}
              resultCount={allocationResultCount}
              onShowList={onShowAllocationList}
              listLoading={allocationListLoading}
            />
          </div>
        )}

        {assignError && (
          <div className="alert-error mt-4" role="alert">
            {assignError}
          </div>
        )}

        {activeCycle ? (
          <form onSubmit={submitAssignSubjectFaculty} className="assign-form mt-6">
            <div className="assign-form-grid">
              <div>
                <label className="input-label" htmlFor="assign-subject">Subject</label>
                <select
                  id="assign-subject"
                  value={assignSubjectFacultyForm.subjectId}
                  onChange={(e) => setAssignSubjectFacultyForm((f) => ({ ...f, subjectId: e.target.value }))}
                  required
                  className="input"
                >
                  <option value="">Select subject</option>
                  {semesterSubjects.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.code ? `${s.code} - ${s.name}` : s.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="input-label" htmlFor="assign-faculty">Faculty</label>
                <select
                  id="assign-faculty"
                  value={assignSubjectFacultyForm.facultyId}
                  onChange={(e) => setAssignSubjectFacultyForm((f) => ({ ...f, facultyId: e.target.value }))}
                  required
                  className="input"
                >
                  <option value="">Select faculty</option>
                  {facultyList.map((f) => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="input-label" htmlFor="assign-division">Division (optional)</label>
                <select
                  id="assign-division"
                  value={assignSubjectFacultyForm.divisionId || ''}
                  onChange={(e) => setAssignSubjectFacultyForm((f) => ({ ...f, divisionId: e.target.value }))}
                  className="input"
                >
                  <option value="">No division</option>
                  {semesterDivisions.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.division_code || d.name}{d.program_code ? ` (${d.program_code})` : d.degree_name ? ` (${d.degree_name})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="input-label" htmlFor="assign-section">Section (optional)</label>
                <input
                  id="assign-section"
                  type="text"
                  value={assignSubjectFacultyForm.section || ''}
                  onChange={(e) => setAssignSubjectFacultyForm((f) => ({ ...f, section: e.target.value }))}
                  maxLength={10}
                  placeholder="e.g. A"
                  className="input"
                />
              </div>

              <div className="assign-form-checkbox">
                <label className="assign-checkbox-label" htmlFor="assign-primary">
                  <input
                    id="assign-primary"
                    type="checkbox"
                    checked={Boolean(assignSubjectFacultyForm.isPrimary)}
                    onChange={(e) => setAssignSubjectFacultyForm((f) => ({ ...f, isPrimary: e.target.checked }))}
                    className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  Primary instructor
                </label>
              </div>

              <div className="assign-form-submit">
                <button type="submit" disabled={assignSubmitting} className="btn-primary w-full sm:w-auto px-6">
                  {assignSubmitting ? 'Adding…' : 'Add to active semester'}
                </button>
              </div>
            </div>
          </form>
        ) : (
          <p className="admin-empty-text mt-6">
            To add new assignments manually, initiate a feedback cycle and activate it under Feedback Configuration.
          </p>
        )}

        <div className="assign-table-section">
          <div className="assign-table-header">
            <h3 className="assign-table-title">
              {browseMode ? 'Subject–faculty allocations' : 'Current assignments for this semester'}
            </h3>
            <span className="admin-badge">{tableRows.length} total</span>
          </div>

          {browseMode && !allocationListShown ? (
            <div className="admin-empty py-8">
              <p className="admin-empty-title">No list loaded</p>
              <p className="admin-empty-text">Choose department and program, then click Show.</p>
            </div>
          ) : tableRows.length === 0 ? (
            <div className="admin-empty py-8">
              <p className="admin-empty-title">No assignments yet</p>
              <p className="admin-empty-text">
                {browseMode
                  ? 'No allocations match these filters for the selected academic year.'
                  : 'Add a subject and faculty using the form above.'}
              </p>
            </div>
          ) : (
            <div className="table-wrap">
              <table className="table assign-table">
                <thead>
                  <tr>
                    {showSemesterColumn && <th>Sem</th>}
                    <th>Subject</th>
                    <th>Code</th>
                    <th>Faculty</th>
                    <th>Department</th>
                    <th>Program</th>
                    <th>Division</th>
                    <th>Section</th>
                    <th>Role</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((a) => (
                    <tr key={a.id}>
                      {showSemesterColumn && <td>{a.semester ?? '—'}</td>}
                      <td className="font-medium">{a.subject_name || '—'}</td>
                      <td>{a.subject_code || '—'}</td>
                      <td>
                        <div>{a.faculty_name || '—'}</div>
                        {a.faculty_employee_id && (
                          <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                            {a.faculty_employee_id}
                          </div>
                        )}
                      </td>
                      <td>{a.department_code || a.subject_department || '—'}</td>
                      <td>{a.program_code || '—'}</td>
                      <td>{a.division_name || a.division_code || '—'}</td>
                      <td>{a.section || '—'}</td>
                      <td>
                        {a.is_primary ? (
                          <span className="assign-role-badge assign-role-badge-primary">Primary</span>
                        ) : (
                          <span className="assign-role-badge">Secondary</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
